import { Close } from "@mui/icons-material";
import {
  Chip,
  createFilterOptions,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Popover,
  TextField,
  Tooltip,
} from "@mui/material";
import PSelect from "components/PSelect";
import {
  CaseClientStatus,
  CaseReviewStatusReason,
  GetCaseReviewStatusReasonsDocument,
  SectionReviewState,
  SectionValueMatchMode,
} from "generated/graphql";
import { useFirmsByLensId } from "helpers/hooks/useAllowedLenses";
import { UsePopover } from "helpers/hooks/usePopover";
import { useAppSelector } from "redux/hooks";
import {
  CaseFilterQueryParam,
  selectCalcProperties,
  selectCaseDisplayGroups,
  selectCasePropertySchemas,
  selectClientLens,
} from "redux/slices/clientLens";
import { selectEnableFirmFeatureSet } from "redux/slices/session";
import { UseCases } from "./useCases";
import { useGroupLocations } from "routes/lens-editor/custom-properties/useGroupLocations";
import { Fragment } from "react/jsx-runtime";
import {
  CaseDisplayGroupItemSchemaType,
  useFindSchemaForCaseDisplayGroupItem,
} from "routes/lens-editor/custom-properties/useFindSchemaForCaseDisplayGroupItem";
import CustomPropertyCell from "components/CustomPropertyCell";
import { deserializeCustomPropertyValue } from "helpers/customProperties";
import { selectOutcomeEnabledFormSections } from "redux/slices/form.slice";
import { REVIEW_STATE_UI } from "SBR/REVIEW_STATE_UI";
import { UserAutoCompleteMultiple } from "components/UserAutocomplete";
import DescriptionTooltip from "components/FormField/DescriptionTooltip";
import StyledAutocomplete from "components/StyledAutocomplete";
import { useEffect, useState } from "react";
import useDebounce from "helpers/useDebounce";
import { enqueueSnackbar } from "notistack";
import { runGqlQuery } from "gqlClient";
import { extractGQLError } from "shared/PatternGraphQLError";

export default function CaseFilters({
  filterPopover,
  lensId,
  staticAssignees,
  casesHook,
  readonly,
}: {
  filterPopover: UsePopover;
  lensId: number;
  staticAssignees: number[];
  casesHook: UseCases;
  readonly: boolean;
}) {
  const clientLens = useAppSelector(selectClientLens);
  const outcomeEnabledFormSections = useAppSelector(selectOutcomeEnabledFormSections);
  const firms = useFirmsByLensId(lensId);
  const showFirms = useAppSelector(selectEnableFirmFeatureSet);
  const caseDisplayGroups = useAppSelector(selectCaseDisplayGroups);
  const groupsByLocation = useGroupLocations(caseDisplayGroups);
  const casePropertySchemas = useAppSelector(selectCasePropertySchemas);
  const calcSchemas = useAppSelector(selectCalcProperties);
  const findItemSchema = useFindSchemaForCaseDisplayGroupItem(calcSchemas, casePropertySchemas);
  const activeCasesLabel = `Active (${clientLens?.casesOverview?.activeCount || 0})`;
  const inactiveCasesLabel = `Inactive (${clientLens?.casesOverview?.inactiveCount || 0})`;

  const [reviewStatusReasonQuery, setReviewStatusReasonQuery] = useState<string>("");
  const reviewStatusReasonQueryDebounced = useDebounce(reviewStatusReasonQuery, 200);
  const [reviewStatusReasons, setReviewStatusReasons] = useState<CaseReviewStatusReason[]>([]);
  useEffect(() => {
    if (reviewStatusReasonQueryDebounced && reviewStatusReasonQueryDebounced.length >= 2) {
      runGqlQuery(GetCaseReviewStatusReasonsDocument, {
        lensId,
        query: reviewStatusReasonQueryDebounced,
        limit: 10,
      })
        .then((response) => setReviewStatusReasons(response.lens.caseReviewStatusReasons))
        .catch((error) => {
          const gql = extractGQLError(error);
          enqueueSnackbar(`Error searching case review status reasons: ${gql?.message || "unknown"}`, {
            variant: "error",
          });
        });
    }
  }, [reviewStatusReasonQueryDebounced]);

  return (
    <Popover
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      open={filterPopover.isOpen}
      anchorEl={filterPopover.anchorEl}
      onClose={filterPopover.onPopoverClose}>
      <div className="p-4 pt-0 w-96 flex flex-col gap-4">
        <div className="flex justify-between border-b border-b-gray-50 py-2 items-center">
          <p className="font-semibold text-sm">Filters</p>
          <IconButton size="small" onClick={filterPopover.onPopoverClose}>
            <Close />
          </IconButton>
        </div>
        {showFirms && (
          <FormControl size="small" disabled={readonly}>
            <InputLabel variant="outlined">Firm</InputLabel>
            <PSelect
              data-pendo="case-table-filter-firm"
              label="Firm"
              value={casesHook.caseFilterParams.plaintiffFirmId || ""}
              onChange={(evt) => {
                const v = evt.target.value as string;
                casesHook.addFilter(CaseFilterQueryParam.FIRM_ID, v || undefined);
              }}>
              <MenuItem value="">Show All</MenuItem>
              {firms.map((firm) => (
                <MenuItem key={firm.id} value={firm.id.toString()}>
                  {firm.name}
                </MenuItem>
              ))}
            </PSelect>
          </FormControl>
        )}
        <FormControl size="small" disabled={readonly}>
          <InputLabel variant="outlined">Has Documents</InputLabel>
          <PSelect
            label="Has Documents"
            value={casesHook.caseFilterParams.hasDocuments ?? ""}
            onChange={(evt) => {
              const v = evt.target.value;
              casesHook.addFilter(CaseFilterQueryParam.HAS_DOCUMENTS, (v as string) || undefined);
            }}>
            <MenuItem value="">Show All</MenuItem>
            <MenuItem value="true">Yes</MenuItem>
            <MenuItem value="false">No</MenuItem>
          </PSelect>
        </FormControl>
        <FormControl size="small" disabled={readonly}>
          <InputLabel variant="outlined">Case Status</InputLabel>
          <PSelect
            data-pendo="case-table-filter-case-status"
            label="Case Status"
            value={casesHook.caseFilterParams.caseStatus !== "all" ? casesHook.caseFilterParams.caseStatus || "" : ""}
            onChange={(evt) => {
              const v = evt.target.value as string;
              casesHook.addFilter(CaseFilterQueryParam.CASE_STATUS, v || undefined);
            }}>
            <MenuItem value="all">Show All</MenuItem>
            <MenuItem value={CaseClientStatus.Active}>{activeCasesLabel}</MenuItem>
            <MenuItem value={CaseClientStatus.Inactive}>{inactiveCasesLabel}</MenuItem>
          </PSelect>
        </FormControl>
        <FormControl size="small" disabled={readonly}>
          <StyledAutocomplete
            data-pendo="case-table-filter-review-status-reason"
            id="review-status-reason-filters"
            size="small"
            fullWidth
            autoComplete
            filterOptions={createFilterOptions({
              ignoreCase: true,
              matchFrom: "any",
              stringify: (opt) => `${opt.reviewStatusReason.slice(0, 20)} (${opt.count})`,
            })}
            autoHighlight
            freeSolo
            value={casesHook.caseFilterParams.reviewStatusReason || ""}
            onInputChange={(_evt, v) => setReviewStatusReasonQuery(v)}
            onChange={(_evt, v) => {
              if (typeof v === "string") {
                casesHook.addFilter(CaseFilterQueryParam.REVIEW_STATUS_REASON, v);
              } else if (v) {
                casesHook.addFilter(
                  CaseFilterQueryParam.REVIEW_STATUS_REASON,
                  (v as CaseReviewStatusReason).reviewStatusReason
                );
              } else {
                casesHook.addFilter(CaseFilterQueryParam.REVIEW_STATUS_REASON, undefined);
              }
            }}
            options={reviewStatusReasons}
            getOptionLabel={(opt) => {
              if (typeof opt === "string") {
                return opt;
              } else {
                return `${opt.reviewStatusReason} (${opt.count})`;
              }
            }}
            renderInput={(params) => <TextField {...params} label="Review Status Reason" fullWidth />}
          />
        </FormControl>

        {groupsByLocation.casesFilter.map((cdg) => (
          <Fragment key={cdg.group.id}>
            <p className="font-semibold text-xs text-gray-400">{cdg.group.name}</p>
            {cdg.group.items.map((item) => {
              const itemSchema = findItemSchema(item);
              const value =
                itemSchema?.schemaType === CaseDisplayGroupItemSchemaType.Property
                  ? casesHook.caseFilterParams.caseProperties.find((cp) => cp.key === itemSchema.key)?.value ?? ""
                  : casesHook.caseFilterParams.caseCalcs.find((c) => c.key === itemSchema?.key)?.value ?? "";
              return itemSchema !== undefined ? (
                <CustomPropertyCell
                  key={item.id}
                  debounce
                  formControlProps={{
                    fullWidth: true,
                  }}
                  textProps={{
                    sx: { width: "100%" },
                  }}
                  numberProps={{
                    sx: { width: "100%" },
                  }}
                  customProperty={itemSchema}
                  value={deserializeCustomPropertyValue(value, itemSchema)}
                  setValue={(v) => {
                    if (itemSchema.schemaType === CaseDisplayGroupItemSchemaType.Property) {
                      casesHook.addPropFilter(itemSchema.key, itemSchema.dataType, v);
                    } else {
                      casesHook.addCalcFilter(itemSchema.key, itemSchema.dataType, v);
                    }
                  }}
                  disabled={readonly}
                />
              ) : (
                <Fragment key={item.id} />
              );
            })}
          </Fragment>
        ))}

        <p className="font-semibold text-xs text-gray-400">Section Filters</p>
        <div className="flex gap-2 items-center">
          <FormControl size="small" className="grow" disabled={readonly}>
            <InputLabel variant="outlined">Sections</InputLabel>
            <PSelect<string[]>
              multiple
              label="Sections"
              value={(casesHook.caseFilterParams.sectionIds || outcomeEnabledFormSections.map((s) => s.id)).map(
                (numericId) => numericId.toString()
              )}
              onChange={(evt) => {
                const v = evt.target.value;
                const asArray = Array.isArray(v) ? v : [v];
                // Check for Show All being selected, if so, clear out all review statuses
                if (asArray.find((status) => status === "") !== undefined) {
                  casesHook.addFilter(CaseFilterQueryParam.SECTION_ID, []);
                } else {
                  casesHook.addFilter(CaseFilterQueryParam.SECTION_ID, asArray);
                }
              }}
              renderValue={(selectedSectionIds) => (
                <div className="flex gap-2 flex-wrap">
                  {selectedSectionIds.map((selected) => (
                    <Chip
                      size="small"
                      key={selected}
                      label={
                        outcomeEnabledFormSections.find((s) => s.id.toString() === selected)?.name ?? "Unknown section"
                      }
                    />
                  ))}
                </div>
              )}>
              <MenuItem value="">All</MenuItem>
              {outcomeEnabledFormSections.map((formSection) => (
                <MenuItem key={formSection.id} value={formSection.id}>
                  {formSection.name}
                </MenuItem>
              ))}
            </PSelect>
          </FormControl>
          <Tooltip
            enterDelay={250}
            title={
              casesHook.caseFilterParams.sectionMatchMode === SectionValueMatchMode.Any
                ? "Any (or at least one) of a case's sections can match"
                : "All of a case's sections must match all the selected filters"
            }>
            <button
              className={`text-xs border border-gray-200 h-full p-1 rounded-md bg-peri-50 ${readonly ? "" : "focus-visible:outline-none hover:bg-peri-100"}`}
              disabled={readonly}
              onClick={() =>
                casesHook.addFilter(
                  CaseFilterQueryParam.SECTION_MATCH_MODE,
                  casesHook.caseFilterParams.sectionMatchMode === SectionValueMatchMode.Any
                    ? SectionValueMatchMode.All
                    : SectionValueMatchMode.Any
                )
              }>
              {casesHook.caseFilterParams.sectionMatchMode === SectionValueMatchMode.Any ? "any" : "all"}
            </button>
          </Tooltip>
        </div>
        <FormControl size="small" disabled={readonly}>
          <InputLabel variant="outlined">Status</InputLabel>
          <PSelect
            multiple
            label="Status"
            value={
              casesHook.caseFilterParams.sectionStatuses || [
                SectionReviewState.Validation,
                SectionReviewState.Review,
                SectionReviewState.Complete,
              ]
            }
            onChange={(evt) => {
              const v = evt.target.value;
              // Check for Show All being selected, if so, clear out all review statuses
              if ((v as string[]).find((status) => status === "") !== undefined) {
                casesHook.addFilter(CaseFilterQueryParam.SECTION_STATUS, []);
              } else {
                casesHook.addFilter(CaseFilterQueryParam.SECTION_STATUS, v as SectionReviewState[]);
              }
            }}
            renderValue={(selectedStates) => (
              <div className="flex gap-2 flex-wrap">
                {(selectedStates as SectionReviewState[]).map((selected) => (
                  <Chip size="small" key={selected} label={REVIEW_STATE_UI[selected].name} />
                ))}
              </div>
            )}>
            <MenuItem value="">Show All</MenuItem>
            <MenuItem value={SectionReviewState.Review}>{REVIEW_STATE_UI[SectionReviewState.Review].name}</MenuItem>
            <MenuItem value={SectionReviewState.Validation}>
              {REVIEW_STATE_UI[SectionReviewState.Validation].name}
            </MenuItem>
            <MenuItem value={SectionReviewState.Complete}>{REVIEW_STATE_UI[SectionReviewState.Complete].name}</MenuItem>
          </PSelect>
        </FormControl>
        {staticAssignees.length === 0 && (
          <div className="flex gap-2 items-center">
            <FormControl size="small" fullWidth disabled={readonly}>
              <UserAutoCompleteMultiple
                disabled={readonly}
                options={casesHook.optionsForUserAutocomplete}
                dataPendo="case-table-filter-assignee"
                label="Assignees"
                selected={casesHook.selectedNamedUsers}
                onChange={(namedUsers) => {
                  casesHook.addFilter(
                    CaseFilterQueryParam.SECTION_ASSIGNEES,
                    namedUsers.map((u) => u.id.toString())
                  );
                }}
              />
            </FormControl>
            <DescriptionTooltip description="The assignee filter shows sections where the selected user is assigned as the Review or QA assignee." />
          </div>
        )}
      </div>
    </Popover>
  );
}
